import React, { useEffect, useMemo, useState } from "react";
import { Select, Divider } from "antd";
import { Hooks } from "@react-awesome-query-builder/ui";

const { useListValuesAutocomplete } = Hooks;
const Option = Select.Option;

const SELECT_WIDTH_OFFSET_RIGHT = 48;
const calcTextWidth = (str) => {
  if (!str) return 0;
  if (typeof document !== "undefined") {
    var div = document.createElement("div");
    div.innerText = str;
    var css = {
      "position": "absolute",
      "float": "left",
      "white-space": "nowrap",
      "visibility": "hidden",
      "font-size": "14px",
      "font-family": "'Helvetica Neue', Helvetica, Arial, sans-serif"
    };
    for (var k in css) {
      div.style[k] = css[k];
    }
    div = document.body.appendChild(div);
    var w = div.offsetWidth;
    document.body.removeChild(div);
    return w;
  }
  return 0;
};

const CustomAutocomplete = (props) => {
  let { config, placeholder, allowCustomValues, customProps, value, readonly, multiple, useAsyncSearch, asyncListValues } = props;

  const [labelValues, setLabelValues] = useState([]);

  // hook
  const {
    open,
    onDropdownVisibleChange,
    onChange,
    isSpecialValue,
    onInputChange,
    inputValue,
    options,
    isLoading,
    aPlaceholder,
    extendOptions,
    getOptionDisabled,
    getOptionLabel,
  } = useListValuesAutocomplete(props, {
    debounceTimeout: 100,
    multiple
  });
  
  // Create a non-mutated copy of options with updated titles from asyncListValues and labelValues
  const safeOptions = (options || []).map(opt => {
    const newOpt = { ...opt };
    if (asyncListValues?.length) {
      const asyncMatch = asyncListValues.find(a => a.value === newOpt.value);
      if (asyncMatch && asyncMatch.children) newOpt.title = asyncMatch.children;
    }
    if (labelValues?.length) {
      const labelMatch = labelValues.find(lv => lv.value === newOpt.value || lv.key === newOpt.value);
      if (labelMatch && labelMatch.label) newOpt.title = labelMatch.label;
    }
    return newOpt;
  });

  const { renderSize } = config.settings;
  const placeholderWidth = calcTextWidth(placeholder);
  const aValue = value && value.length ? value : undefined;
  const width = aValue ? null : placeholderWidth + SELECT_WIDTH_OFFSET_RIGHT;

  // Add any pre-selected values from aValue into the safeOptions list so they show up
  let mergedOptions = [...safeOptions];
  if (aValue && aValue.length) {
    const prependedOptions = [];
    aValue.forEach(val => {
      // Remove it from the fetched list if it already exists there so we don't duplicate
      mergedOptions = mergedOptions.filter(o => o.value !== val);
      
      // Try to find the title from our resolved labelValues
      const labelItem = labelValues?.find(lv => lv.value === val || lv.key === val);
      prependedOptions.push({
        value: val,
        title: labelItem ? labelItem.label : val
      });
    });
    // Add the currently selected items to the very top of the list
    mergedOptions = [...prependedOptions, ...mergedOptions];
  }

  const filteredOptions = extendOptions(mergedOptions);

  const optionsMaxWidth = useMemo(() => {
    return filteredOptions.reduce((max, option) => {
      return Math.max(max, calcTextWidth(option.title, null));
    }, 0);
  }, [filteredOptions]);
  
  const dropdownWidth = optionsMaxWidth + SELECT_WIDTH_OFFSET_RIGHT;

  const apiSearchEndpoints = {
    _county: "location/get-county-names-map",
    _congress: "location/get-cd-names-map",
    _cbsa: "location/get-cbsa-names-map"
  };

  const aValueStr = JSON.stringify(aValue);
  useEffect(() => {
    let cancelled = false;
    getLabels(aValue).then((vals) => {
      if (!cancelled) setLabelValues(vals || []);
    }).catch(() => {
      if (!cancelled) setLabelValues([]);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aValueStr, config.settings.extras, props.field]);
  
  const style = {
    minWidth: width,
    width: "100%",
  };
  const dropdownStyle = {
    width: dropdownWidth,
  };

  // Get labels for geo-boundaries
  const getLabels = async (values) => {
    if (!values || !values.length) return [];
    let type;
    const searchTerms = Object.keys(apiSearchEndpoints);

    for (const term of searchTerms) {
      if (props.field && props.field.includes(term)) {
        type = term;
      }
    }
    const apiUrl = apiSearchEndpoints[type];
    if (!type || !apiUrl) return [];
    
    // Call the same fetch method that complex-filter-builder sets up in settings.extras
    const res = await config.settings.extras(apiUrl, values);
    let list = Object.entries(res || {}).map((item) => {
      // res is { id: label } map
      return { value: item[0], label: item[1] };
    });
    
    return list;
  };

  const mode = !multiple ? undefined : (allowCustomValues ? "tags" : "multiple");
  const dynamicPlaceholder = !readonly ? aPlaceholder : "";

  // rendering special 'Load more' option has side effect: on change rc-select will save its title as internal value in own state
  const renderedOptions = filteredOptions?.filter(option => !option.specialValue).map((option) => (
    <Option 
      key={option.value} 
      value={option.value} 
      disabled={getOptionDisabled(option)}
    >
      {getOptionLabel(option)}
    </Option>
  ));

  const onSpecialClick = (specialValue) => () => {
    const option = filteredOptions.find(opt => opt.specialValue === specialValue);
    onChange(null, option);
  };

  const specialOptions = filteredOptions?.filter(option => !!option.specialValue).map((option) => (
    <button 
      type="button"
      style={{ padding: "5px 10px", display: "block", cursor: "pointer", border: "none", background: "transparent", color: "#1890ff", width: "100%", textAlign: "left" }}
      key={option.specialValue} 
      disabled={getOptionDisabled(option)}
      onClick={onSpecialClick(option.specialValue)}
    >
      {getOptionLabel(option)}
    </button>
  ));

  const aOnSelect = async (label, option) => {
    if (isSpecialValue(option)) {
      await onChange(label, option);
    }
  };

  const aOnSearch = async (newInputValue) => {
    if (newInputValue === "" && !open) {
      return;
    }
    if (onInputChange) {
      await onInputChange(null, newInputValue);
    }
  };

  const aOnChange = async (label, option) => {
    if (!isSpecialValue(option)) {
      await onChange(label, option);
    }
  };

  const dropdownRender = (menu) => (
    <div>
      {menu}
      {specialOptions.length > 0
        && <>
          <Divider style={{ margin: "0px" }}/>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {specialOptions}
          </div>
        </>
      }
    </div>
  );
  return (
    <Select
      tokenSeparators={[',', ' ', '\t', '\n']}

      filterOption={useAsyncSearch ? false : true}
      dropdownRender={dropdownRender}
      allowClear={false}
      notFoundContent={isLoading ? "Loading..." : null}
      disabled={readonly}
      mode={mode}
      style={customProps?.style || style}
      dropdownStyle={customProps?.dropdownStyle || dropdownStyle}
      key={"widget-autocomplete"}
      dropdownMatchSelectWidth={customProps?.dropdownMatchSelectWidth || false}
      placeholder={customProps?.placeholder || dynamicPlaceholder}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onChange={aOnChange}
      onSelect={aOnSelect}
      onSearch={aOnSearch}
      searchValue={inputValue}
      showSearch
      size={renderSize}
      loading={isLoading}
      value={aValue}
      open={open}
      {...customProps}
    >
      {renderedOptions}
    </Select>
  );
};

export default CustomAutocomplete;
